import {
  ScmIntegrationsApi,
  scmIntegrationsApiRef,
  ScmAuth,
} from '@backstage/integration-react';
import {
  AnyApiFactory,
  configApiRef,
  createApiFactory,
  discoveryApiRef,
  gitlabAuthApiRef,
  identityApiRef,
  oauthRequestApiRef,
  type IdentityApi,
} from '@backstage/core-plugin-api';
import { GitlabAuth } from '@backstage/core-app-api';
import {
  catalogGraphApiRef,
  DefaultCatalogGraphApi,
} from '@backstage/plugin-catalog-graph';

/**
 * Settings → Authentication Providers only calls `gitlabAuthApi.signOut()`, which clears
 * the OAuth session but leaves the in-app `UserIdentity` and cookie session intact, so
 * the shell stays mounted with no redirect. Chaining `identityApi.signOut()` matches the
 * user menu and triggers cookie cleanup + full navigation to the sign-in entry URL.
 *
 * `UserIdentity.signOut()` also calls `authApi.signOut()`; we skip the extra identity
 * hop when that re-entrant call runs so we do not recurse.
 */
let gitlabAuthSignOutInvokedFromIdentityApi = false;

function wrapGitLabAuthWithFullSessionSignOut(
  gitlab: ReturnType<typeof GitlabAuth.create>,
  identityApi: IdentityApi,
): ReturnType<typeof GitlabAuth.create> {
  return new Proxy(gitlab, {
    get(target, prop, receiver) {
      if (prop === 'signOut') {
        return async () => {
          if (gitlabAuthSignOutInvokedFromIdentityApi) {
            await target.signOut();
            return;
          }
          await target.signOut();
          gitlabAuthSignOutInvokedFromIdentityApi = true;
          try {
            await identityApi.signOut();
          } finally {
            gitlabAuthSignOutInvokedFromIdentityApi = false;
          }
        };
      }
      return Reflect.get(target, prop, receiver);
    },
  }) as ReturnType<typeof GitlabAuth.create>;
}

export const apis: AnyApiFactory[] = [
  createApiFactory({
    api: scmIntegrationsApiRef,
    deps: { configApi: configApiRef },
    factory: ({ configApi }) => ScmIntegrationsApi.fromConfig(configApi),
  }),
  ScmAuth.createDefaultApiFactory(),
  // EntityRelationsGraph / useRelations need this; legacy catalogGraphPlugin is not loaded with createApp(alpha).
  createApiFactory({
    api: catalogGraphApiRef,
    deps: {},
    factory: () => new DefaultCatalogGraphApi(),
  }),
  createApiFactory({
    api: gitlabAuthApiRef,
    deps: {
      discoveryApi: discoveryApiRef,
      oauthRequestApi: oauthRequestApiRef,
      configApi: configApiRef,
      identityApi: identityApiRef,
    },
    factory: ({ discoveryApi, oauthRequestApi, configApi, identityApi }) =>
      wrapGitLabAuthWithFullSessionSignOut(
        GitlabAuth.create({
          configApi,
          discoveryApi,
          oauthRequestApi,
          environment: configApi.getOptionalString('auth.environment'),
        }),
        identityApi,
      ),
  }),
];
